// outbox.js — durable offline queue for calendar writes.
//
// When the v2 orchestrator can't reach a provider (CalendarProviderError code
// OFFLINE or TRANSIENT), the planned mutation is parked here instead of
// thrown away. The drainer empties the queue when the network comes back
// (`online` event), when a sync run starts, and on a 30-second timer.
//
// After 5 unsuccessful attempts an entry is parked and a notification fires.
// Parked entries stay on disk and can be drained later by the user via a
// "retry parked items" UX (not yet built).
//
// Storage: `NotesAppCalendarOutbox` IndexedDB DB, single object store
// `entries` keyed by a UUID. Mirrors the pattern in workspace-session-vault.js
// (idb-helpers.js for the plumbing); we don't encrypt entries because they
// only ever contain user-authored markdown that's already in their daily
// notes — leaking the same content to disk twice doesn't change the threat
// model.
//
// Entry shape:
//   {
//     id,               // UUID — primary key
//     accountId,        // provider account
//     calendarId,       // destination calendar
//     op,               // 'create' | 'update' | 'delete'
//     payload,          // op-specific: see encode/applyOne below
//     attempts,         // count of failed runs
//     nextRetryAt,      // ms epoch
//     createdAt,        // ms epoch
//     parked,           // true once attempts >= MAX_ATTEMPTS
//     lastError,        // human-readable error message (for diagnostics)
//   }

import { Settings } from '../settings/settings-store.js';
import { openDB, reqDone, txDone } from '../utils/idb-helpers.js';

const DB_NAME = 'NotesAppCalendarOutbox';
const DB_VERSION = 1;
const STORE_NAME = 'entries';
const MAX_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 30_000;
const DRAIN_INTERVAL_MS = 30_000;
const TRANSIENT_CODES = new Set(['OFFLINE', 'TRANSIENT', 'RATE_LIMITED']);

let _dbPromise: any = null;
let _drainTimer: any = null;
let _draining: any = null;
let _onlineHandler: any = null;

/** @returns {string} a fresh entry id. */
function _genId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `oub-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** @returns {Promise<any>} the (cached) outbox IndexedDB handle. */
async function _getDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = openDB(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('nextRetryAt', 'nextRetryAt', { unique: false });
      store.createIndex('accountId', 'accountId', { unique: false });
    }
  }).catch((err) => {
    _dbPromise = null;
    throw err;
  });
  return _dbPromise;
}
/**
 * @param {any} db
 * @param {IDBTransactionMode} mode
 * @returns {{ tx: IDBTransaction, store: IDBObjectStore }}
 */
function _tx(db: any, mode: any) {
  const tx = db.transaction(STORE_NAME, mode);
  return { tx, store: tx.objectStore(STORE_NAME) };
}
/**
 * @param {IDBRequest} r
 * @returns {Promise<any>}
 */
function _req(r: any): Promise<any> {
  return reqDone(r);
}
/**
 * @param {IDBTransaction} t
 * @returns {Promise<void>}
 */
function _txDone(t: any) {
  return txDone(t);
}

// ── Public API ────────────────────────────────────────────────────────

/**
 * @param {any} spec - { id?, accountId, calendarId?, op, payload? }
 * @returns {Promise<any>} the persisted entry.
 */
async function enqueue(spec: any) {
  if (!spec?.accountId || !spec.op) {
    throw new TypeError('outbox.enqueue: accountId + op required');
  }
  const now = Date.now();
  const entry = {
    id: spec.id || _genId(),
    accountId: spec.accountId,
    calendarId: spec.calendarId || null,
    op: spec.op,
    payload: spec.payload || {},
    attempts: 0,
    nextRetryAt: now,
    createdAt: now,
    parked: false,
    lastError: null as any,
  };
  const db = await _getDB();
  const { tx, store } = _tx(db, 'readwrite');

  // Dedup: replace any non-parked entry that targets the same event
  // (same accountId + calendarId + providerId). Three queued edits to
  // the same event would otherwise drain in order — the second and
  // later updates would ETag-conflict against the server's post-first-
  // edit state and park, silently losing the user's last edit.
  // Coalescing keeps only the latest enqueue; parked entries are left
  // alone because the user (or a future "retry parked" UX) decides
  // their fate explicitly.
  const providerId = spec.payload?.providerId;
  if (providerId) {
    const all = await _req(store.getAll());
    for (const existing of all) {
      if (existing.parked) continue;
      if (existing.accountId !== entry.accountId) continue;
      if (existing.calendarId !== entry.calendarId) continue;
      const existingProviderId = existing.payload?.providerId;
      if (existingProviderId !== providerId) continue;
      store.delete(existing.id);
    }
  }

  store.put(entry);
  await _txDone(tx);
  return entry;
}

/**
 * @param {any} [opts] - { accountId?, parked?, dueBefore? }
 * @returns {Promise<any[]>} matching entries.
 */
async function list(opts?: any) {
  const db = await _getDB();
  const { store } = _tx(db, 'readonly');
  const all = await _req(store.getAll());
  if (!opts) return all;
  let out = all;
  if (opts.accountId) out = out.filter((e: any) => e.accountId === opts.accountId);
  if (opts.parked === true) out = out.filter((e: any) => e.parked);
  if (opts.parked === false) out = out.filter((e: any) => !e.parked);
  if (opts.dueBefore != null)
    out = out.filter((e: any) => !e.parked && e.nextRetryAt <= opts.dueBefore);
  return out;
}

/**
 * @param {string} id
 * @returns {Promise<void>}
 */
async function remove(id: any) {
  const db = await _getDB();
  const { tx, store } = _tx(db, 'readwrite');
  store.delete(id);
  await _txDone(tx);
}

/** @returns {Promise<number>} the total entry count. */
async function size() {
  return (await list()).length;
}

// Reset a parked entry so the next drain picks it up. Clears the
// attempt counter, lastError, and reschedules to "now" — equivalent
// to a fresh enqueue but preserves the entry's id and original
// createdAt so a UI tracking it doesn't lose its handle.
/**
 * @param {string} id
 * @returns {Promise<boolean>} true if the entry existed and was reset.
 */
async function unpark(id: any) {
  const db = await _getDB();
  const { tx, store } = _tx(db, 'readwrite');
  const existing = await _req(store.get(id));
  if (!existing) {
    await _txDone(tx);
    return false;
  }
  existing.parked = false;
  existing.attempts = 0;
  existing.lastError = null;
  existing.nextRetryAt = Date.now();
  store.put(existing);
  await _txDone(tx);
  return true;
}

// Apply one outbox entry through the registry. Returns true if the entry
// was successfully consumed; false if it should remain in the queue.
/**
 * @param {any} entry - the outbox entry to apply.
 * @param {any} registry - the provider registry.
 * @returns {Promise<boolean>} true if the entry was consumed/dropped.
 */
async function _applyOne(entry: any, registry: any) {
  const inst = registry?.get?.(entry.accountId);
  if (!inst) {
    // The registry doesn't currently have an instance for this account.
    // Two reasons:
    //   (a) the user removed the account from Settings → drop the entry,
    //       there's nowhere to send it and retrying is pointless.
    //   (b) the registry hasn't finished hydrating (e.g. vault is still
    //       unlocking on app start) → leave the entry alone so the next
    //       drain (after hydration completes) can retry.
    // Distinguish by consulting the persisted account list directly via
    // the ESM `Settings` export. settings-store imports nothing heavy
    // (session / notes-broadcast / encryption-state / feedback), so the
    // static import forms no cycle; outbox.test.js mocks settings-store
    // with a module-scoped holder rather than reassigning a global.
    const accounts = Settings?.get?.('calendars.accounts', []) || [];
    const stillRegistered = accounts.some((a: any) => a && a.accountId === entry.accountId);
    if (!stillRegistered) {
      await remove(entry.id);
      return true;
    }
    // Hydration race — leave the entry due, the next drain picks it up.
    return false;
  }
  const { op, payload } = entry;
  try {
    if (op === 'create') {
      await inst.createEvent(entry.calendarId, payload.event);
    } else if (op === 'update') {
      await inst.updateEvent(entry.calendarId, payload.providerId, payload.event, {
        etag: payload.etag,
      });
    } else if (op === 'delete') {
      await inst.deleteEvent(entry.calendarId, payload.providerId, { etag: payload.etag });
    } else {
      // Unknown op — park rather than spin forever.
      return await _park(entry, `unknown op: ${op}`);
    }
    await remove(entry.id);
    return true;
  } catch (e: any) {
    const code = e?.code;
    if (TRANSIENT_CODES.has(code)) {
      // Transient: reschedule with exponential backoff.
      return await _reschedule(entry, e);
    }
    // Hard failure (NOT_FOUND, PRECONDITION_FAILED, AUTH_REQUIRED, …).
    // NOT_FOUND on delete is idempotent success.
    if (op === 'delete' && code === 'NOT_FOUND') {
      await remove(entry.id);
      return true;
    }
    return await _park(entry, e?.message || String(code || e));
  }
}

/**
 * @param {any} entry
 * @param {any} err
 * @returns {Promise<boolean>} always false (entry stays queued or parked).
 */
async function _reschedule(entry: any, err: any) {
  const attempts = entry.attempts + 1;
  if (attempts >= MAX_ATTEMPTS) {
    // Don't increment in _park too — pass the bumped count through so a
    // parked entry shows `attempts === MAX_ATTEMPTS`, not MAX + 1.
    return _park({ ...entry, attempts }, err?.message || 'max attempts', {
      incrementAttempts: false,
    });
  }
  const backoff = BASE_BACKOFF_MS * 2 ** (attempts - 1);
  const next = {
    ...entry,
    attempts,
    nextRetryAt: Date.now() + backoff,
    lastError: err?.message || String(err?.code || err),
  };
  const db = await _getDB();
  const { tx, store } = _tx(db, 'readwrite');
  store.put(next);
  await _txDone(tx);
  return false;
}

/**
 * @param {any} entry
 * @param {string} reason
 * @param {any} [opts] - { incrementAttempts? }
 * @returns {Promise<boolean>} always false.
 */
async function _park(entry: any, reason: any, opts?: any) {
  const bump = opts?.incrementAttempts !== false;
  const next = {
    ...entry,
    attempts: bump ? entry.attempts + 1 : entry.attempts,
    parked: true,
    lastError: reason,
  };
  const db = await _getDB();
  const { tx, store } = _tx(db, 'readwrite');
  store.put(next);
  await _txDone(tx);
  // sendNotification stays a runtime window read this wave: importing
  // ui/notifications.js here pulls its transitive app-state → note-editor →
  // CM6 widget chain into outbox's isolated unit env (no window.CM6 stub),
  // breaking outbox.test.js. Deferred until that chain is lighter.
  if (typeof window.sendNotification === 'function') {
    window.sendNotification(
      'Calendar sync — item parked',
      `Could not sync a calendar change after ${MAX_ATTEMPTS} attempts: ${reason}`,
      `outbox-park-${entry.id}`
    );
  }
  return false;
}

// Drain all due (non-parked) entries through the registry. Single-flight:
// a second concurrent call awaits the in-flight Promise. Mirrors the
// `_calendarSyncPromise` pattern in calendar-sync.js.
/**
 * @param {any} [registry] - defaults to window.ProviderRegistry.
 * @returns {Promise<{ processed: number, remaining: number }>}
 */
async function drain(registry: any) {
  if (_draining) return _draining;
  const R = registry || (typeof window !== 'undefined' && window.ProviderRegistry);
  _draining = (async () => {
    try {
      if (!R) return { processed: 0, remaining: await size() };
      const due = await list({ dueBefore: Date.now(), parked: false });
      let processed = 0;
      for (const entry of due) {
        const ok = await _applyOne(entry, R);
        if (ok) processed++;
      }
      return { processed, remaining: (await list({ parked: false })).length };
    } finally {
      _draining = null;
    }
  })();
  return _draining;
}

// Start the background drainer. Idempotent. Hooks the `online` event so
// entries flush the moment connectivity returns.
/**
 * @param {any} [registry]
 * @returns {void}
 */
function start(registry: any) {
  if (_drainTimer) return;
  _drainTimer = setInterval(() => {
    drain(registry).catch((e) => console.warn('[outbox] drain failed:', e));
  }, DRAIN_INTERVAL_MS);
  if (typeof window !== 'undefined' && window.addEventListener) {
    _onlineHandler = () => {
      drain(registry).catch((e) => console.warn('[outbox] drain on online failed:', e));
    };
    window.addEventListener('online', _onlineHandler);
  }
}

/** @returns {void} */
function stop() {
  if (_drainTimer) {
    clearInterval(_drainTimer);
    _drainTimer = null;
  }
  if (_onlineHandler && typeof window !== 'undefined' && window.removeEventListener) {
    window.removeEventListener('online', _onlineHandler);
    _onlineHandler = null;
  }
}

// Build an outbox entry from a CalendarProviderError + the original args
// the caller was about to use. The orchestrator (or transport) invokes
// this from its retry-or-enqueue branch.
/**
 * @param {{ accountId: any, calendarId: any, op: string, args: any }} spec
 * @param {any} err - the CalendarProviderError that triggered enqueue.
 * @returns {any} an enqueue spec, or null if not transient/unknown op.
 */
function encodeFailure({ accountId, calendarId, op, args }: any, err: any) {
  const code = err?.code;
  if (!TRANSIENT_CODES.has(code)) return null; // only transient → enqueue
  let payload: any;
  if (op === 'create') payload = { event: args.event };
  else if (op === 'update')
    payload = { providerId: args.providerId, event: args.event, etag: args.etag };
  else if (op === 'delete') payload = { providerId: args.providerId, etag: args.etag };
  else return null;
  return { accountId, calendarId, op, payload };
}

// Test reset — clears the DB cache + in-flight drain so a fresh test
// session can start from scratch. The actual IDB is wiped by fake-indexeddb
// between tests in setupFiles, so we don't need to drop the DB here.
/** @returns {void} */
function _resetForTests() {
  _dbPromise = null;
  _draining = null;
  stop();
}

// Phase 4.7: real ESM. `CalendarOutbox` is the aggregate api; the settings
// panel imports it directly. It is also mirrored onto `window.CalendarOutbox`
// as a runtime seam for calendar-sync.js, which reaches the outbox off window
// (it lives outside this partition).
export const CalendarOutbox = {
  enqueue,
  list,
  remove,
  size,
  drain,
  start,
  stop,
  unpark,
  encodeFailure,
  _resetForTests,
  // Constants exposed for tests; not part of the stable surface.
  MAX_ATTEMPTS,
  BASE_BACKOFF_MS,
  DRAIN_INTERVAL_MS,
  TRANSIENT_CODES,
};

if (typeof window !== 'undefined') window.CalendarOutbox = CalendarOutbox;
